import { CandyMachineAccount } from '../candy-machine';
import { GatewayStatus, useGateway } from '@civic/solana-gateway-react';
import { useEffect, useState, useRef } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import {
    findGatewayToken,
    getGatewayTokenAddressForOwnerAndGatekeeperNetwork,
    onGatewayTokenChange,
    removeAccountChangeListener,
} from '@identity.com/solana-gateway-ts';


export const MintButton = ({
                               onMint,
                               candyMachine,
                               isMinting,
                               setIsMinting,
                               isActive,
                           }: {
    onMint: () => Promise<void>;
    candyMachine?: CandyMachineAccount;
    isMinting: boolean;
    setIsMinting: (val: boolean) => void;
    isActive: boolean;
}) => {
    const wallet = useWallet();
    const connection = useConnection();
    const [verified, setVerified] = useState(false);
    const { requestGatewayToken, gatewayStatus } = useGateway();
    const [webSocketSubscriptionId, setWebSocketSubscriptionId] = useState(-1);
    const [clicked, setClicked] = useState(false);

    const getMintButtonContent = () => {
        if (candyMachine?.state.isSoldOut) {
            return 'SOLD OUT';
        } else if (isMinting) {
            return<>
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Minting ...
            </>
        } else if (
            candyMachine?.state.isPresale ||
            candyMachine?.state.isWhitelistOnly
        ) {
            return 'Whitelist Mint';
        }

        return 'Mint';
    };

    useEffect(() => {
        const mint = async () => {
            await removeAccountChangeListener(
                connection.connection,
                webSocketSubscriptionId,
            );
            await onMint();

            setClicked(false);
            setVerified(false);
        };
        if (verified && clicked) {
            mint();
        }
    }, [
        verified,
        clicked,
        connection.connection,
        onMint,
        webSocketSubscriptionId,
    ]);

    const previousGatewayStatus = usePrevious(gatewayStatus);
    useEffect(() => {
        const fromStates = [
            GatewayStatus.NOT_REQUESTED,
            GatewayStatus.REFRESH_TOKEN_REQUIRED,
        ];
        const invalidToStates = [...fromStates, GatewayStatus.UNKNOWN];
        if (
            fromStates.find(state => previousGatewayStatus === state) &&
            !invalidToStates.find(state => gatewayStatus === state)
        ) {
            setIsMinting(true);
        }
        console.log('change: ', gatewayStatus);
    }, [setIsMinting, previousGatewayStatus, gatewayStatus]);

    return (
        <button
            className="btn btn-primary m-2 mx-auto bg-brand font-bold text-2xl text-brand-text rounded-lg w-full hover:scale-105 hover:bg-brand-hover text-center flex"
            disabled={isMinting || !isActive}
            onClick={async () => {
                if (candyMachine?.state.isActive && candyMachine?.state.gatekeeper) {
                    const network =
                        candyMachine.state.gatekeeper.gatekeeperNetwork.toBase58();
                    if (network === 'ignREusXmGrscGNUesoU9mxfds9AiYTezUKex2PsZV6') {
                        if (gatewayStatus === GatewayStatus.ACTIVE) {
                            await onMint();
                        } else {
                            // setIsMinting(true);
                            await requestGatewayToken();
                            console.log('after: ', gatewayStatus);
                        }
                    } else if (
                        network === 'ttib7tuX8PTWPqFsmUFQTj78MbRhUmqxidJRDv4hRRE' ||
                        network === 'tibePmPaoTgrs929rWpu755EXaxC7M3SthVCf6GzjZt'
                    ) {
                        setClicked(true);
                        const gatewayToken = await findGatewayToken(
                            connection.connection,
                            wallet.publicKey!,
                            candyMachine.state.gatekeeper.gatekeeperNetwork,
                        );

                        if (gatewayToken?.isValid()) {
                            await onMint();
                        } else {
                            window.open(
                                `https://verify.encore.fans/?gkNetwork=${network}`,
                                '_blank',
                            );

                            const gatewayTokenAddress =
                                await getGatewayTokenAddressForOwnerAndGatekeeperNetwork(
                                    wallet.publicKey!,
                                    candyMachine.state.gatekeeper.gatekeeperNetwork,
                                );

                            setWebSocketSubscriptionId(
                                onGatewayTokenChange(
                                    connection.connection,
                                    gatewayTokenAddress,
                                    () => setVerified(true),
                                    'confirmed',
                                ),
                            );
                        }
                    } else {
                        setClicked(false);
                        throw new Error(`Unknown Gatekeeper Network: ${network}`);
                    }
                } else {
                    await onMint();
                    setClicked(false);
                }
            }}
        >
            {getMintButtonContent()}
        </button>
    );
};

function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
